// 원격 세션 탭 관리 훅 — 세션 창(session.html)과 메인 창의 대체 경로(팝업 차단 시)가 **같은 로직**을 쓴다.
//
// 탭 하나 = RtcClient 하나. 탭의 수명(접속 중 → 연결됨 → 실패/종료)을 상태로 들고, 사용자가 탭을 닫으면
// 클라이언트를 정리한다. 실패·종료된 탭은 바로 지우지 않고 사유를 보여 준다(다시 연결 / 닫기).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APPROVAL_FAIL_MESSAGE, RtcClient } from '../rtc';
import { isRememberableCode, rememberCode } from '../recentCodes';
import { forgetTrustedDevice, markTrustedDevice, tokenSubject } from '../trustedDevices';
import { ensureUsableToken } from './tokenBroker';

/** 비밀번호 없이 접속했더니 거부됐을 때의 안내. */
const TRUST_REVOKED_MESSAGE =
  '이 기기가 비밀번호 없는 접속을 더 이상 허용하지 않습니다(무인 비밀번호가 바뀌었거나 허용이 해제됨). 목록에서 다시 [접속]을 눌러 비밀번호를 넣어 주세요.';
import { moveById } from './tabOrder';
import {
  isTabLive,
  requestKey,
  requestLabel,
  type Session,
  type SessionRequest,
  type SessionTab,
} from './types';

/** 세션 종료 사유(서버 reason) → 사용자 문구. 모르는 값은 원문을 붙여 보여 준다. */
const CLOSE_REASON_MESSAGE: Readonly<Record<string, string>> = {
  time_limit: '사용 시간이 종료되었습니다. 다시 연결할 수 있습니다.',
  // 관리자 조치(2026-09-15 사용자 관리) — 네트워크 문제로 오해해 재접속을 반복하지 않게 원인을 밝힌다.
  user_disabled: '관리자가 이 계정의 사용을 중지해 세션이 종료되었습니다.',
  credentials_reset: '관리자가 비밀번호를 초기화해 세션이 종료되었습니다. 새 비밀번호로 다시 로그인하세요.',
  // 같은 기기에 새 접속이 승인됨(서버 SESSION_CLOSE_REASON.SESSION_REPLACED, 2026-10-23) — 기기는 세션을 하나만 받는다.
  session_replaced: '같은 기기에 새 접속이 시작되어 이 연결이 끊겼습니다.',
};

function closeMessage(reason: string): string {
  return CLOSE_REASON_MESSAGE[reason] ?? (reason ? `세션 종료: ${reason}` : '세션이 종료되었습니다.');
}

export interface SessionManager {
  tabs: SessionTab[];
  activeId: string | null;
  setActiveId(id: string): void;
  /** 요청을 새 탭으로 연다. 같은 대상이 이미 접속 중·연결됨이면 그 탭으로 이동만 한다. */
  open(req: SessionRequest): void;
  /** 탭을 닫는다(세션도 끊는다). */
  close(id: string): void;
  /** 실패·종료된 탭을 같은 요청으로 다시 연결한다. */
  retry(id: string): void;
  /** 모든 세션을 끊는다(창을 닫을 때). */
  closeAll(): void;
  /** 탭 순서를 바꾼다(같은 창 탭 줄 안에서 끌어 놓기) — 세션은 그대로 둔다. @param to 옮긴 뒤 위치(0 = 맨 앞) */
  move(id: string, to: number): void;
}

export function useSessionManager(): SessionManager {
  const [tabs, setTabs] = useState<SessionTab[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  // 콜백들이 최신 탭 목록을 보도록(중복 판정·닫은 뒤 다음 탭 선택) 참조 사본을 유지한다.
  const tabsRef = useRef<SessionTab[]>([]);
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);
  const clients = useRef(new Map<string, RtcClient>());
  const seq = useRef(0);

  const update = useCallback((id: string, fn: (t: SessionTab) => SessionTab) => {
    setTabs((ts) => ts.map((t) => (t.id === id ? fn(t) : t)));
  }, []);
  const patchSession = useCallback(
    (id: string, patch: Partial<Session>) =>
      update(id, (t) => (t.session ? { ...t, session: { ...t.session, ...patch } } : t)),
    [update],
  );

  /** 탭의 클라이언트를 정리하고 단계를 바꾼다(실패·종료 공통). */
  const finish = useCallback(
    (id: string, phase: 'failed' | 'ended', message: string) => {
      clients.current.get(id)?.close();
      clients.current.delete(id);
      update(id, (t) => ({ ...t, phase, message, session: undefined }));
    },
    [update],
  );

  /** 탭별 시작 차수 — 토큰을 기다리는 사이 [다시 연결]을 또 누르거나 탭을 닫으면 앞선 시작을 버린다(클라이언트 둘·닫힌 탭 접속 방지). */
  const startGen = useRef(new Map<string, number>());

  const launch = useCallback(
    (id: string, req: SessionRequest) => {
      const anon = req.kind === 'anon';
      let deadline: number | undefined;
      const client = new RtcClient(
        req.kind === 'user' ? req.token : '',
        {
        onLog: (m) => console.log('[rtc]', m),
        onState: (state, ok) =>
          update(id, (t) =>
            t.session
              ? { ...t, session: { ...t.session, state, ok: ok ?? t.session.ok } }
              : { ...t, message: state },
          ),
        onAllowedMs: (ms) => {
          deadline = Date.now() + ms;
        },
        onStream: (stream) => {
          // 즉석 접속 성공 → 로그인 화면의 "최근 접속" 에 코드를 기억한다(비밀번호는 저장하지 않는다 — recentCodes.ts).
          // 로그인 접속도 **코드로** 들어갔으면 기억한다([연결] 화면의 최근 목록 — 2026-10-23 사용자 요청). 기기 목록의 내부 ID 는 빼고.
          if (req.kind === 'anon') rememberCode(req.code);
          else if (isRememberableCode(req.target)) rememberCode(req.target);
          update(id, (t) => ({
            ...t,
            phase: 'live',
            message: undefined,
            // clipCaps: 채널 hello 가 영상보다 먼저 왔을 수 있다(그때는 patchSession 이 담을 세션이 없었다) → 여기서도 채운다.
            session: {
              client,
              stream,
              stats: null,
              state: '연결됨',
              ok: true,
              anon,
              deadline,
              clipCaps: client.clip?.capabilities ?? undefined,
              fileCaps: client.files?.capabilities ?? undefined,
              qualityCaps: client.quality?.capabilities ?? undefined,
              chatCaps: client.chat?.capabilities ?? undefined,
              drawCaps: client.draw?.capabilities ?? undefined,
              // 승인 통지는 영상보다 먼저 온다 — 그때 받아 둔 허용 기능(§14.19.2).
              features: client.grantedFeatures ?? undefined,
            },
          }));
        },
        onStats: (stats) => patchSession(id, { stats }),
        onMonitors: (monitors, currentMonitor) => patchSession(id, { monitors, currentMonitor }),
        onControlAllowed: (controlAllowed) => patchSession(id, { controlAllowed }),
        onClipReady: (clipCaps) => patchSession(id, { clipCaps }),
        onFileReady: (fileCaps) => patchSession(id, { fileCaps }),
        onQualityReady: (qualityCaps) => patchSession(id, { qualityCaps }),
        // 적용 결과가 오면 직전 오류는 지운다(성공했는데 빨간 문구가 남아 있으면 안 된다).
        onQualityApplied: (qualityApplied) =>
          patchSession(id, { qualityApplied, qualityError: undefined }),
        onQualityError: (qualityError) => patchSession(id, { qualityError }),
        // 채팅(§9.6) — 능력만 여기서 받는다. 대화는 화면(useChat)이 채널에 직접 리스너를 단다.
        onChatReady: (chatCaps) => patchSession(id, { chatCaps }),
        onDrawReady: (drawCaps) => patchSession(id, { drawCaps }),
        onClosed: (reason) => finish(id, 'ended', closeMessage(reason)),
        // 🔴 기억은 **기기가 허용했다고 알렸을 때만** 한다 — 체크했다는 것만으로 기억하면, 기기가 허용을 만들지 않은
        //    경우(세션 비밀번호로 승인)에도 다음 접속을 비밀번호 없이 보내 엉뚱하게 거부된다(2026-09-26 종단 시험에서 발견).
        onTrustedViewer: () => {
          if (req.kind === 'user') markTrustedDevice(tokenSubject(req.token), req.target);
        },
        onFailed: (message) => {
          // 비밀번호 없이 보낸 접속이 **거부됐다** = 기기가 허용을 거뒀다(무인 비밀번호 변경·[모두 해제]).
          // 기억을 지워 다음에는 다시 묻게 하고, 왜 거부됐는지 말한다(응답 없음 등 다른 실패는 기억을 건드리지 않는다).
          if (req.kind === 'user' && req.trusted && message === APPROVAL_FAIL_MESSAGE.rejected) {
            forgetTrustedDevice(tokenSubject(req.token), req.target);
            finish(id, 'failed', TRUST_REVOKED_MESSAGE);
            return;
          }
          finish(id, 'failed', message);
        },
        },
        // 수퍼유저가 고른 조직(§14.19.1) — 세션 REST 에 싣는다.
        req.kind === 'user' ? (req.orgContext ?? '') : '',
      );
      clients.current.set(id, client);
      const run =
        req.kind === 'user'
          ? client.connect(req.target, req.password, req.remember === true)
          : client.connectAnon(req.code, req.password);
      run.catch((e: Error) => finish(id, 'failed', e.message));
    },
    [update, patchSession, finish],
  );

  /**
   * 탭의 접속을 시작한다. 로그인 접속은 먼저 **쓸 수 있는 토큰**을 확보한다(tokenBroker.ts — 2026-10-23 결함:
   * 15분 넘은 탭의 [다시 연결]·탭 옮기기가 만료 토큰으로 거부돼 "아무 반응 없음" 으로 보였다). 새 토큰을 받으면 탭의 요청도
   * 갈아 끼워 다음 [다시 연결]·탭 옮기기가 그것을 쓰게 한다. 토큰이 아직 넉넉하면 기다림 없이 바로 시작한다.
   */
  const start = useCallback(
    (id: string, req: SessionRequest) => {
      const gen = (startGen.current.get(id) ?? 0) + 1;
      startGen.current.set(id, gen);
      const current = () => startGen.current.get(id) === gen && tabsRef.current.some((t) => t.id === id);
      if (req.kind !== 'user') {
        launch(id, req);
        return;
      }
      void ensureUsableToken(req.token).then(
        (token) => {
          if (!current()) {
            return;
          }
          const r = token === req.token ? req : { ...req, token };
          if (r !== req) {
            update(id, (t) => ({ ...t, request: r }));
          }
          launch(id, r);
        },
        (e: Error) => {
          if (current()) {
            finish(id, 'failed', e.message);
          }
        },
      );
    },
    [launch, update, finish],
  );

  const open = useCallback(
    (req: SessionRequest) => {
      const key = requestKey(req);
      const existing = tabsRef.current.find((t) => t.key === key && isTabLive(t));
      if (existing) {
        setActiveId(existing.id);
        return;
      }
      seq.current += 1;
      const id = `s${seq.current}`;
      const tab: SessionTab = { id, key, label: requestLabel(req), request: req, phase: 'connecting', message: '접속 중…' };
      tabsRef.current = [...tabsRef.current, tab]; // 같은 틱에 연속 호출돼도 중복 판정이 되게 즉시 반영
      setTabs((ts) => [...ts, tab]);
      setActiveId(id);
      start(id, req);
    },
    [start],
  );

  const close = useCallback((id: string) => {
    clients.current.get(id)?.close();
    clients.current.delete(id);
    const rest = tabsRef.current.filter((t) => t.id !== id);
    tabsRef.current = rest;
    setTabs(rest);
    setActiveId((cur) => (cur === id ? (rest[rest.length - 1]?.id ?? null) : cur));
  }, []);

  const retry = useCallback(
    (id: string) => {
      const tab = tabsRef.current.find((t) => t.id === id);
      if (!tab) {
        return;
      }
      clients.current.get(id)?.close();
      update(id, (t) => ({ ...t, phase: 'connecting', message: '다시 연결 중…', session: undefined }));
      setActiveId(id);
      start(id, tab.request);
    },
    [start, update],
  );

  const closeAll = useCallback(() => {
    for (const c of clients.current.values()) {
      c.close();
    }
    clients.current.clear();
  }, []);

  const move = useCallback((id: string, to: number) => {
    const next = moveById(tabsRef.current, id, to);
    tabsRef.current = next; // close 와 같이 즉시 반영(연속 호출에도 최신 순서 기준)
    setTabs(next);
  }, []);

  return useMemo(
    () => ({ tabs, activeId, setActiveId, open, close, retry, closeAll, move }),
    [tabs, activeId, open, close, retry, closeAll, move],
  );
}
